import { useCallback, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../context';
import { useCreateAbuseReportMutation } from '../../../services';
import { Button, ButtonType, IconLabel, Modal, TextAreaField } from '../../components';
import { ABUSE_REPORT_COOKIE_KEY } from '../../constants';
import { cookieOptions, optArrItem } from '../../utils';

import classes from './useAbuseReportPopup.module.scss';

export enum AbuseReportType {
  Post = 'post',
  Comment = 'comment',
  User = 'user',
  Chat = 'chat',
  Message = 'message',
  Event = 'event',
}

export enum AbuseReason {
  Accessibility_Feedback = 'accessibility_feedback',
  Praise_Feedback = 'praise_feedback',
  General_Feedback = 'general_feedback',
  Wrong_or_Misleading_Information = 'wrong_or_misleading_information',
  Suggest_Edit = 'suggest_edit',
  SomethingElse = 'something_else',
}

interface AbuseReportProps {
  type: AbuseReportType;
  userID?: number;
  userName?: string;
  userEmail?: string;
  storyId?: number;
  storyName?: string;
  postId?: number;
  postType?: string;
  postTitle?: string;
  authorId?: number;
  authorName?: string;
  commentId?: number;
  commentText?: string;
  commentAuthorId?: number;
  commentAuthorName?: string;
  chatId?: number;
  chatName?: string;
  chatType?: string;
  messageId?: string;
  messageText?: string;
  messageAuthorId?: string;
  messageAuthorName?: string;
  messageType?: string;
  eventId?: number;
  eventName?: string;
}

export const useAbuseReportPopup = ({
  type,
  userID,
  userEmail,
  userName,
  storyId,
  storyName,
  postId,
  postType,
  postTitle,
  authorId,
  authorName,
  commentId,
  commentText,
  commentAuthorId,
  commentAuthorName,
  chatId,
  chatName,
  chatType,
  messageId,
  messageText,
  messageAuthorId,
  messageAuthorName,
  messageType,
  eventId,
  eventName,
}: AbuseReportProps) => {
  const { t } = useTranslation();

  const { channelId, channelName, user_id, name, email } = useContext(UserContext).userInfo.body;

  const [createAbuseReport] = useCreateAbuseReportMutation();

  const [isOpen, setIsOpen] = useState(false);

  const [reason, setReason] = useState<AbuseReason>();

  const [reporterComment, setReporterComment] = useState<string>('');

  const [cookies, setCookie] = useCookies([ABUSE_REPORT_COOKIE_KEY]);

  const {
    reportedUserIds = [],
    reportedPostIds = [],
    reportedCommentIds = [],
    reportedChatIds = [],
    reportedMessageIds = [],
    reportedEventIds = [],
  } = {
    ...cookies[ABUSE_REPORT_COOKIE_KEY],
  };

  const onClose = useCallback(() => {
    setIsOpen(false);
    setReason(undefined);
    setReporterComment('');
  }, []);

  const reported = useMemo(() => {
    switch (type) {
      case AbuseReportType.User:
        return reportedUserIds.includes(userID);
      case AbuseReportType.Post:
        return reportedPostIds.includes(postId);
      case AbuseReportType.Comment:
        return reportedCommentIds.includes(commentId);
      case AbuseReportType.Chat:
        return reportedChatIds.includes(chatId);
      case AbuseReportType.Message:
        return reportedMessageIds.includes(messageId);
      case AbuseReportType.Event:
        return reportedEventIds.includes(eventId);
      default:
        return false;
    }
  }, [
    chatId,
    commentId,
    eventId,
    messageId,
    postId,
    reportedChatIds,
    reportedCommentIds,
    reportedEventIds,
    reportedMessageIds,
    reportedPostIds,
    reportedUserIds,
    type,
    userID,
  ]);

  const submitHandler = useCallback(async () => {
    if (!reason) {
      return;
    }

    onClose();

    try {
      await toast.promise(
        createAbuseReport({
          type,
          reason,
          channelId,
          channelName,
          reporterComment,
          reportedBy: { id: user_id, email, name },
          ...(userID && { userID }),
          ...(userEmail && { userEmail }),
          ...(userName && { userName }),
          ...(storyId && { storyId }),
          ...(storyName && { storyName }),
          ...(postId && { postId }),
          ...(postType && { postType }),
          ...(postTitle && { postTitle }),
          ...(authorId && { authorId }),
          ...(authorName && { authorName }),
          ...(commentId && { commentId }),
          ...(commentText && { commentText }),
          ...(commentAuthorId && { commentAuthorId }),
          ...(commentAuthorName && { commentAuthorName }),
          ...(chatId && { chatId }),
          ...(chatName && { chatName }),
          ...(chatType && { chatType }),
          ...(messageId && { messageId }),
          ...(messageText && { messageText }),
          ...(messageAuthorId && { messageAuthorId }),
          ...(messageAuthorName && { messageAuthorName }),
          ...(messageType && { messageType }),
          ...(eventId && { eventId }),
          ...(eventName && { eventName }),
        }).unwrap(),
        {
          pending: t('abuseReport.submitting'),
          success: t('abuseReport.submitted'),
          error: t('abuseReport.submit-error'),
        }
      );
    } catch (_) {
      return;
    }

    setCookie(
      ABUSE_REPORT_COOKIE_KEY,
      {
        reportedUserIds: [...reportedUserIds, ...optArrItem(type === AbuseReportType.User, userID)],
        reportedPostIds: [...reportedPostIds, ...optArrItem(type === AbuseReportType.Post, postId)],
        reportedCommentIds: [
          ...reportedCommentIds,
          ...optArrItem(type === AbuseReportType.Comment, commentId),
        ],
        reportedChatIds: [...reportedChatIds, ...optArrItem(type === AbuseReportType.Chat, chatId)],
        reportedMessageIds: [
          ...reportedMessageIds,
          ...optArrItem(type === AbuseReportType.Message, messageId),
        ],
        reportedEventIds: [
          ...reportedEventIds,
          ...optArrItem(type === AbuseReportType.Event, eventId),
        ],
      },
      cookieOptions()
    );
  }, [
    authorId,
    authorName,
    channelId,
    channelName,
    chatId,
    chatName,
    chatType,
    commentAuthorId,
    commentAuthorName,
    commentId,
    commentText,
    createAbuseReport,
    email,
    eventId,
    eventName,
    messageAuthorId,
    messageAuthorName,
    messageId,
    messageText,
    messageType,
    name,
    onClose,
    postId,
    postTitle,
    postType,
    reason,
    reportedChatIds,
    reportedCommentIds,
    reportedEventIds,
    reportedMessageIds,
    reportedPostIds,
    reportedUserIds,
    reporterComment,
    setCookie,
    storyId,
    storyName,
    t,
    type,
    userEmail,
    userID,
    userName,
    user_id,
  ]);

  const bodyContent = useMemo(() => {
    if (!reason) {
      return (
        <div className={classes['abuse-report__reasons']}>
          {Object.entries(AbuseReason).map(([key, value]) => {
            return (
              <span
                key={key}
                className={classes['abuse-report__reasons-item']}
                onClick={() => setReason(value)}
              >
                {t(`abuseReportReasons.${value}`)}
                <IconLabel iconId={'arrow-right'} iconSize={18} singleColor />
              </span>
            );
          })}
        </div>
      );
    }

    return (
      <>
        <TextAreaField
          placeholder={t('abuseReport.comment-placeholder')}
          value={reporterComment}
          maxLength={2000}
          onChange={({ target }) => setReporterComment(target.value)}
          disableLineBreak
        />

        <div className={classes['abuse-report__buttons']}>
          <Button
            type={ButtonType.secondary}
            label={t('common.back')}
            onClick={() => setReason(undefined)}
          />
          <Button type={ButtonType.primary} label={t('common.submit')} onClick={submitHandler} />
        </div>
      </>
    );
  }, [reason, reporterComment, submitHandler, t]);

  const title = t('abuseReport.title');

  const body = useMemo(() => {
    return (
      <div className={classes['abuse-report']}>
        <div className={classes['abuse-report__title']}>{title}</div>
        <div className={classes['abuse-report__subTitle']}>{t('abuseReport.sub-title')}</div>
        {bodyContent}
      </div>
    );
  }, [bodyContent, t, title]);

  const abuseReportModal = useMemo(() => {
    if (!isOpen) {
      return null;
    }

    return <Modal isOpen={isOpen} body={body} onClose={onClose} ariaLabel={title} alignTop />;
  }, [body, isOpen, onClose, title]);

  const abuseReportHandler = useCallback(async () => {
    if (reported) {
      toast.info(t('abuseReport.was-reported'));
      return;
    }

    setIsOpen(true);
  }, [reported, t]);

  return { abuseReportModal, abuseReportHandler };
};
